/**
 * Cache Synchronization Utilities
 * Handles cache updates after mutations and sync on reconnection
 */

import { cacheDelete, extractEntityFromResponse, cacheCreate, cacheUpdate } from './cacheHelpers';
import {
  getProjects,
  getNotes,
  getCorkboards,
  getTimelines,
  getCharacters,
  getFamilies,
} from './dataClient';
import { isOnline } from '../apiRequests/offlineQueue';

/**
 * Update cache after a mutation (create/update/delete)
 * This is called immediately after a successful mutation to keep cache in sync
 */
export const syncCacheAfterMutation = async (entityType, response, operation = 'update') => {
  try {
    if (operation === 'delete') {
      // For delete, response might be the ID directly or an object with id
      const entityId =
        response?.data?._id || response?.data?.id || response?._id || response?.id || response;
      if (entityId) {
        await cacheDelete(entityType, entityId);
        return true;
      }
      return false;
    }

    // For create/update, check if response has error
    if (response?.error) {
      console.warn('[cacheSync] Error in response, skipping cache update');
      return false;
    }

    // For create/update, extract entity from response
    const entity = extractEntityFromResponse(response);
    if (entity) {
      if (operation === 'create') {
        await cacheCreate(entityType, entity);
      } else {
        await cacheUpdate(entityType, entity);
      }
      return true;
    }

    return false;
  } catch (error) {
    console.error('[cacheSync] Error syncing cache after mutation:', error);
    return false;
  }
};

/**
 * Sync cache on reconnection
 * Fetches fresh data from server and updates cache
 */
export const syncCacheOnReconnect = async () => {
  if (!isOnline()) {
    console.log('[cacheSync] Still offline, skipping sync');
    return { success: false, error: 'Offline' };
  }

  console.log('[cacheSync] Syncing cache on reconnection...');

  try {
    // Fetch all projects (this will trigger cache updates)
    const projectsResult = await getProjects({ useCache: false });

    if (projectsResult.error) {
      console.error('[cacheSync] Error fetching projects on reconnect:', projectsResult.error);
      return { success: false, error: projectsResult.error };
    }

    const projects = projectsResult.data || [];

    // For each project, refresh related data
    const refreshPromises = projects.map(async (project) => {
      const projectId = project._id || project.id;

      // Refresh notes, corkboards, timelines, characters, families for this project
      await Promise.all([
        getNotes(projectId, { useCache: false }),
        getCorkboards(projectId, { useCache: false }),
        getTimelines(projectId, { useCache: false }),
        getCharacters(projectId, { useCache: false }),
        getFamilies(projectId, { useCache: false }),
      ]);
    });

    await Promise.all(refreshPromises);

    console.log('[cacheSync] Cache sync on reconnect complete');
    return { success: true };
  } catch (error) {
    console.error('[cacheSync] Error during cache sync on reconnect:', error);
    return { success: false, error };
  }
};

/**
 * Initialize cache sync listeners
 */
let reconnectHandler = null;

export const initCacheSync = () => {
  if (reconnectHandler) {
    console.warn('[cacheSync] Cache sync already initialized');
    return;
  }

  // Listen for online event to sync cache
  reconnectHandler = () => {
    if (isOnline()) {
      // Delay to ensure connection is stable
      setTimeout(() => {
        syncCacheOnReconnect().catch((err) => {
          console.error('[cacheSync] Error in reconnect handler:', err);
        });
      }, 1000);
    }
  };

  window.addEventListener('online', reconnectHandler);
  console.log('[cacheSync] Cache sync initialized');
};

/**
 * Cleanup cache sync listeners
 */
export const cleanupCacheSync = () => {
  if (reconnectHandler) {
    window.removeEventListener('online', reconnectHandler);
    reconnectHandler = null;
    console.log('[cacheSync] Cache sync cleaned up');
  }
};

export default {
  syncAfterMutation: syncCacheAfterMutation,
  syncOnReconnect: syncCacheOnReconnect,
  init: initCacheSync,
  cleanup: cleanupCacheSync,
};
